import { useCallback } from "react";
import { useLocation, useSearch } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Brain, Palette, Package, Users, FileText, Upload } from "lucide-react";
import { BrandVoicePanel } from "@/components/intelligence/BrandVoicePanel";
import { ProductsPanel } from "@/components/intelligence/ProductsPanel";
import { AudiencesPanel } from "@/components/intelligence/AudiencesPanel";
import { DocumentsPanel } from "@/components/intelligence/DocumentsPanel";
import { ImportPanel } from "@/components/intelligence/ImportPanel";

const TABS = [
  { value: "brand-voice", label: "Voz da Marca", icon: Palette },
  { value: "products", label: "Produtos", icon: Package },
  { value: "audiences", label: "Públicos-Alvo", icon: Users },
  { value: "documents", label: "Documentos & FAQs", icon: FileText },
  { value: "import", label: "Importar", icon: Upload },
] as const;

type TabValue = typeof TABS[number]["value"];

function readTabFromQuery(search: string): TabValue {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const tab = params.get("tab");
  if (tab && TABS.some((t) => t.value === tab)) return tab as TabValue;
  return "brand-voice";
}

export default function Intelligence() {
  const [location, navigate] = useLocation();
  // useSearch escuta tanto pushState/replaceState do wouter quanto popstate
  // do browser, então a tab é sempre derivada da URL — sem state duplicado.
  const search = useSearch();
  const activeTab = readTabFromQuery(search);

  const setActiveTab = useCallback((tab: TabValue) => {
    const pathname = location.split("?")[0];
    const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    if (params.get("tab") === tab) return;
    params.set("tab", tab);
    navigate(`${pathname}?${params.toString()}`, { replace: true });
  }, [location, search, navigate]);

  const handleSwitchTab = useCallback((tab: string) => {
    if (TABS.some((t) => t.value === tab)) setActiveTab(tab as TabValue);
  }, [setActiveTab]);

  return (
    <PermissionGuard permission="whatsapp:knowledge_base">
      <DashboardLayout>
        <div className="max-w-6xl mx-auto space-y-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Brain className="w-6 h-6 text-indigo-600" />
              Inteligência da Empresa
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Base única de conhecimento usada pela IA em todos os canais (WhatsApp, E-mail, Atendimento, CRM).
            </p>
          </div>

          <Tabs value={activeTab} onValueChange={(v) => handleSwitchTab(v)} className="space-y-6">
            <TabsList className="h-auto flex-wrap justify-start gap-1 bg-transparent p-0">
              {TABS.map((t) => {
                const Icon = t.icon;
                return (
                  <TabsTrigger
                    key={t.value}
                    value={t.value}
                    className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary border data-[state=active]:border-primary/30 px-3 py-2"
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {t.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            <TabsContent value="brand-voice"><BrandVoicePanel /></TabsContent>
            <TabsContent value="products"><ProductsPanel /></TabsContent>
            <TabsContent value="audiences"><AudiencesPanel /></TabsContent>
            <TabsContent value="documents"><DocumentsPanel /></TabsContent>
            <TabsContent value="import"><ImportPanel onSwitchTab={handleSwitchTab} /></TabsContent>
          </Tabs>
        </div>
      </DashboardLayout>
    </PermissionGuard>
  );
}
